import type { GlossToken, Interpretation } from './types';

/**
 * Parse the interpreter's reply into the JSON contract the UI runs on.
 *
 * Strips code fences, slices to the outer braces, uppercases signs, drops
 * empties, and throws if no gloss survives — the error row is the only failure
 * surface. Shared by the server (validating the model's output) and the client
 * (validating what came back over the wire), so the contract is enforced once.
 */
export function parseInterpretation(raw: unknown): Interpretation {
  const data = typeof raw === 'string' ? JSON.parse(sliceToJson(raw)) : raw;
  if (!data || typeof data !== 'object') throw new Error('interpreter returned no object');

  const record = data as Record<string, unknown>;
  const gloss = normaliseGloss(record.gloss);
  if (!gloss.length) throw new Error('interpreter returned no gloss');

  return {
    gloss,
    english: typeof record.english === 'string' ? record.english.trim() : '',
    note: typeof record.note === 'string' ? record.note.trim() : '',
  };
}

/** Reduce a raw model response to the outermost JSON object it contains. */
export function sliceToJson(raw: string): string {
  const text = String(raw ?? '')
    .trim()
    .replace(/^```(?:json)?/i, '')
    .replace(/```$/, '')
    .trim();

  const open = text.indexOf('{');
  const close = text.lastIndexOf('}');
  if (open === -1 || close === -1 || close < open) {
    throw new Error('interpreter returned no JSON object');
  }
  return text.slice(open, close + 1);
}

function normaliseGloss(value: unknown): GlossToken[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry): GlossToken => {
      if (typeof entry === 'string') return { sign: entry.trim().toUpperCase() };
      if (!entry || typeof entry !== 'object') return { sign: '' };

      const record = entry as Record<string, unknown>;
      const sign = typeof record.sign === 'string' ? record.sign.trim().toUpperCase() : '';
      const nmf = typeof record.nmf === 'string' ? record.nmf.trim() : '';
      return nmf ? { sign, nmf } : { sign };
    })
    .filter((token) => token.sign.length > 0);
}

/**
 * Stage type steps down as the sign phrase grows, so a long phrase still fits
 * the placeholder frame on one or two lines.
 */
export function signLength(sign: string): 'short' | 'medium' | 'long' {
  if (sign.length > 18) return 'long';
  if (sign.length > 11) return 'medium';
  return 'short';
}
